import { useCallback, useContext, useEffect, useState } from "react";
import AuthContext from "@/contexts/AuthContext";
import { PlayersService } from "@/core/http/services/PlayersService";
import { AuthService } from "@/core/http/services/AuthService";

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const getInitialAvatarUrl = (userLoggedData) =>
  userLoggedData?.player?.avatarUrl ||
  userLoggedData?.player?.avatar ||
  userLoggedData?.player?.photoUrl ||
  userLoggedData?.avatarUrl ||
  "";

const useProfile = () => {
  const { userLoggedData, updateUserFromPlayer } = useContext(AuthContext);
  const playerId = userLoggedData?.playerId ?? null;

  const [loading, setLoading] = useState({
    profile: false,
    avatar: false,
    password: false,
  });
  const [error, setError] = useState({ type: null, message: null });
  const [success, setSuccess] = useState({ type: null, message: null });

  const [avatarUrl, setAvatarUrl] = useState(() => getInitialAvatarUrl(userLoggedData));
  const [profileData, setProfileData] = useState(() => ({
    name: userLoggedData?.name || "Jogador",
    phone: userLoggedData?.phone || "",
    birthDate: toDateInputValue(userLoggedData?.birthDate),
    email: userLoggedData?.email || "",
  }));

  useEffect(() => {
    setAvatarUrl(getInitialAvatarUrl(userLoggedData));
    setProfileData({
      name: userLoggedData?.name || "Jogador",
      phone: userLoggedData?.phone || "",
      birthDate: toDateInputValue(userLoggedData?.birthDate),
      email: userLoggedData?.email || "",
    });
  }, [userLoggedData]);

  const getPlayer = useCallback(async () => {
    if (!playerId) return;

    setLoading((prev) => ({ ...prev, profile: true }));

    try {
      const { status, data } = await PlayersService.getById(playerId);

      if (status !== 200 || !data) return;

      setProfileData({
        name: data.name || "",
        phone: data.phone || "",
        birthDate: toDateInputValue(data.birthDate),
        email: data.email || "",
      });

      const url = data.avatarUrl || data.avatar || data.photoUrl || "";
      if (url) setAvatarUrl(url);
    } finally {
      setLoading((prev) => ({ ...prev, profile: false }));
    }
  }, [playerId]);

  const saveProfile = useCallback(async () => {
    setLoading((prev) => ({ ...prev, profile: true }));
    setError({ type: null, message: null });
    setSuccess({ type: null, message: null });

    try {
      const payload = {
        name: profileData.name?.trim(),
        birthDate: profileData.birthDate || null,
        phone: profileData.phone?.trim(),
      };

      const { status, data, message } = await PlayersService.patchMe(payload);

      if (data?.error || status !== 200) {
        setError({
          type: "profile",
          message: message || "Erro ao atualizar seus dados.",
        });
        return false;
      }

      setProfileData({
        name: data.name || profileData.name,
        phone: data.phone || profileData.phone,
        birthDate: toDateInputValue(data.birthDate) || profileData.birthDate,
        email: data.email || profileData.email,
      });

      await updateUserFromPlayer(data);

      setSuccess({ type: "profile", message: "Dados atualizados com sucesso." });
      return true;
    } catch (err) {
      setError({
        type: "profile",
        message: err?.message || "Erro inesperado ao atualizar seus dados.",
      });
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, profile: false }));
    }
  }, [profileData, updateUserFromPlayer]);

  const uploadAvatar = useCallback(
    async (file) => {
      if (!playerId || !file) return false;

      setLoading((prev) => ({ ...prev, avatar: true }));
      setError({ type: null, message: null });
      setSuccess({ type: null, message: null });

      try {
        const { status, data, message } = await PlayersService.uploadAvatar(
          playerId,
          file,
        );

        if (data?.error || status !== 200) {
          setError({
            type: "avatar",
            message: message || "Erro ao atualizar a foto.",
          });
          return false;
        }

        const nextUrl =
          data?.avatarUrl || data?.url || data?.avatar || data?.photoUrl || "";
        if (nextUrl) setAvatarUrl(nextUrl);
        if (data?.avatarUrl) {
          await updateUserFromPlayer(data);
        }
        setSuccess({ type: "avatar", message: "Foto atualizada com sucesso." });
        return true;
      } catch (err) {
        setError({
          type: "avatar",
          message: err?.message || "Erro inesperado ao atualizar a foto.",
        });
        return false;
      } finally {
        setLoading((prev) => ({ ...prev, avatar: false }));
      }
    },
    [playerId, updateUserFromPlayer],
  );

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    setLoading((prev) => ({ ...prev, password: true }));
    setError({ type: null, message: null });
    setSuccess({ type: null, message: null });

    try {
      const payload = {
        currentPassword,
        newPassword,
      };

      const { status, data, message } = await AuthService.patchPassword(payload);

      if (data?.error || status !== 200) {
        setError({
          type: "password",
          message: message || "Erro ao atualizar a senha.",
        });
        return false;
      }

      setSuccess({ type: "password", message: "Senha atualizada com sucesso." });
      return true;
    } catch (err) {
      setError({
        type: "password",
        message: err?.message || "Erro inesperado ao atualizar a senha.",
      });
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, password: false }));
    }
  }, []);

  const initialize = useCallback(async () => {
    await Promise.all([getPlayer()]);
  }, [getPlayer]);

  return {
    playerId,
    profileData,
    setProfileData,
    avatarUrl,
    loading,
    error,
    success,
    initialize,
    saveProfile,
    uploadAvatar,
    changePassword,
  };
};

export default useProfile;
